'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useMyCIOAuthOptional } from '@/lib/mycio/MyCIOAuthContext'
import {
  MYCIO_LANGUAGE_COOKIE,
  detectMyCIOLanguageFromNavigator,
  getMyCIOLanguage,
  normalizeMyCIOLanguage,
  type MyCIOLanguageCode,
} from '@/lib/mycio/language'
import { isMyCIORtlLanguage } from '@/lib/mycio/translations'
import { DEFAULT_NUMBER_LOCALE, resolveNumberLocale } from '@/lib/mycio/numberFormat'

type MyCIOLanguageContextValue = {
  language: MyCIOLanguageCode
  // The Intl locale numbers, percentages and money are written in: the site language, refined by the
  // browser's region for that same language ('de' on a de-CH browser -> 'de-CH').
  numberLocale: string
  setLanguage: (language: MyCIOLanguageCode) => Promise<void>
}

const MyCIOLanguageContext = createContext<MyCIOLanguageContextValue | null>(null)

function readCookieLanguage(): MyCIOLanguageCode | null {
  if (typeof document === 'undefined') return null
  const parts = document.cookie.split(';').map((part) => part.trim())
  const raw = parts.find((part) => part.startsWith(`${MYCIO_LANGUAGE_COOKIE}=`))?.split('=')[1]
  return normalizeMyCIOLanguage(raw ? decodeURIComponent(raw) : null)
}

function writeCookieLanguage(language: MyCIOLanguageCode) {
  if (typeof document === 'undefined') return
  document.cookie = `${MYCIO_LANGUAGE_COOKIE}=${encodeURIComponent(language)}; Path=/; Max-Age=31536000; SameSite=Lax`
}

export function MyCIOLanguageProvider({
  children,
  initialLanguage,
}: {
  children: ReactNode
  // Explicit starting language that WINS over cookie/navigator/stored-preference. Used by the public
  // /plan/[token] share page: the sharer bakes ?lang=<code> into the link so the recipient reads the plan
  // in the chosen language. When set, we do NOT read the cookie/navigator or fetch the (other user's)
  // stored preference — the shared language is authoritative for that view.
  initialLanguage?: MyCIOLanguageCode
}) {
  // Auth is OPTIONAL: the public share page mounts this provider without an auth provider.
  const auth = useMyCIOAuthOptional()
  const session = auth?.session ?? null
  // `loading` is true until GoTrue has restored (or ruled out) a session. Without it this component
  // cannot tell "signed out" from "not known yet", and fetches once for each — see below.
  const authResolving = auth?.loading ?? false
  const pinned = Boolean(initialLanguage)
  const [language, setLanguageState] = useState<MyCIOLanguageCode>(initialLanguage ?? 'en')

  useEffect(() => {
    if (pinned) return
    const initial = readCookieLanguage() ?? detectMyCIOLanguageFromNavigator()
    setLanguageState(initial)
  }, [pinned])

  useEffect(() => {
    document.documentElement.lang = language
    document.documentElement.dir = isMyCIORtlLanguage(language) ? 'rtl' : 'ltr'
  }, [language])

  useEffect(() => {
    if (pinned) return // a shared/pinned language must not be overridden by the viewer's stored preference
    // WAIT FOR AUTH BEFORE ASKING (measured 2026-09-12). This effect keys on the access token, which
    // is null on mount and then changes when the session restores — so it fired TWICE: once
    // anonymously and once authenticated. The anonymous call cannot return a member's stored
    // preference, so it was pure waste, and it was not free: the browser waterfall showed both
    // landing inside the dashboard's opening burst, at 4.8s and 7.0s, competing with the
    // `/api/mycio/plans` call the first paint actually waits on. Dropping the first is one fewer
    // request in the only window where requests are contended.
    if (authResolving) return
    let cancelled = false
    const headers: HeadersInit = session?.access_token
      ? { Authorization: `Bearer ${session.access_token}` }
      : {}

    fetch('/api/mycio/preferences/language', { headers })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { language?: string } | null) => {
        if (cancelled || !data?.language) return
        const normalized = normalizeMyCIOLanguage(data.language)
        if (!normalized) return
        setLanguageState(normalized)
        writeCookieLanguage(normalized)
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [session?.access_token, pinned, authResolving])

  const setLanguage = useCallback(
    async (nextLanguage: MyCIOLanguageCode) => {
      const normalized = getMyCIOLanguage(nextLanguage).code
      setLanguageState(normalized)
      writeCookieLanguage(normalized)

      if (!session?.access_token) return
      await fetch('/api/mycio/preferences/language', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ language: normalized }),
      }).catch(() => {})
    },
    [session?.access_token],
  )

  // The browser's own tags are read AFTER mount, so the first client render matches the server's
  // (which has no navigator) and hydration sees the same figures; a de-CH browser then refines 'de'.
  const [browserTags, setBrowserTags] = useState<readonly string[]>([])
  useEffect(() => {
    if (typeof navigator === 'undefined') return
    setBrowserTags(navigator.languages?.length ? [...navigator.languages] : [navigator.language])
  }, [])
  const numberLocale = useMemo(() => resolveNumberLocale(language, browserTags), [language, browserTags])

  const value = useMemo(() => ({ language, numberLocale, setLanguage }), [language, numberLocale, setLanguage])

  return (
    <MyCIOLanguageContext.Provider value={value}>
      {children}
    </MyCIOLanguageContext.Provider>
  )
}

/**
 * The site language for UI that may render OUTSIDE the provider (the root layout's offline screen, skip links,
 * the global error page): the provider's language when there is one, otherwise the language cookie (read after
 * mount, so the server render and hydration agree on English first). Never throws.
 */
export function useMyCIOLanguageOrCookie(): MyCIOLanguageCode {
  const ctx = useContext(MyCIOLanguageContext)
  const [cookie, setCookie] = useState<MyCIOLanguageCode | null>(null)
  useEffect(() => {
    if (!ctx) setCookie(readCookieLanguage())
  }, [ctx])
  return ctx?.language ?? cookie ?? 'en'
}

export function useMyCIOLanguage(): MyCIOLanguageContextValue {
  const ctx = useContext(MyCIOLanguageContext)
  if (!ctx) throw new Error('useMyCIOLanguage must be used within MyCIOLanguageProvider')
  return ctx
}

/**
 * The Intl locale this member's numbers, percentages, money and dates are written in — pass it to every
 * formatter in src/lib/mycio/numberFormat.ts. Read from context (never a module-level value, which
 * concurrent server renders would share); English (en-US) outside a language provider.
 */
export function useNumberLocale(): string {
  return useContext(MyCIOLanguageContext)?.numberLocale ?? DEFAULT_NUMBER_LOCALE
}
